import React, { useEffect, useRef, useState } from 'react';
import './ReusableForm.scss';
import Form from '../UI/Form/Form';
import FormSuccessMessage from '../UI/Form/FormSuccessMessage/FormSuccessMessage';
import { useForm } from 'react-hook-form';
import ErrorMessage from '../Footer/NewsletterSignUp/ErrorMessage/ErrorMessage';
import { emailPattern } from '../../util/regex/emailPattern';
import CTAButton from '../UI/CTA/CTAButton/CTAButton';
import Select from '../UI/Select/Select';
import { countriesList } from '../../util/countriesList';
import { phonePattern } from '../../util/regex/phonePattern';
import Loader from '../UI/Icons/Loader/Loader';

/**
 * Renders the reusable form
 * @param {string} backgroundColor
 * @param {string} textColor
 * @param {string} title
 * @param {string} subtitle
 * @param {string} formTitle
 * @param {string} ctaText
 * @param {string} successTitle
 * @param {string} successMessage
 * @param {string} disclaimer
 * @param {string} listTitle
 * @param {array} items
 * @param {string} formGuid
 * @param {string} anchor
 * @param {boolean} isLandingPage
 * @param {array} formFields
 * @param elementChildren
 * @returns {JSX.Element}
 * @constructor
 */
const ReusableForm = ({
  backgroundColor = 'blue',
  textColor = 'white',
  title,
  subtitle,
  formTitle,
  ctaText,
  successTitle,
  successMessage,
  disclaimer,
  listTitle,
  items,
  formGuid,
  anchor = '',
  isLandingPage = false,
  elementChildren,
}) => {
  const { register, handleSubmit, errors, trigger, formState } = useForm({ mode: 'onBlur' });
  const [formComplete, setFormComplete] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const formParent = useRef();

  useEffect(() => {
    // This assumes that if there are no elementChildren, we are just loading the hard-coded form.
    if (!elementChildren && loading) {
      setLoading(false);
    } else {
      const loader = setInterval(() => {
        if (formParent.current.querySelector('.hs-input')) {
          clearInterval(loader);
          customFormStyling();
        }
      }, 200);

      const customFormStyling = () => {
        const inputWrappers = formParent.current.querySelectorAll('.input');
        const labels = formParent.current.querySelectorAll('label[id^="label-"]');
        const title = formParent.current.querySelector('.form-title');
        const errors = formParent.current.querySelectorAll('.hs-error-msg');
        const richtext = formParent.current.querySelectorAll('.hs-richtext');
        const submit = formParent.current.querySelectorAll('.hs-submit input');
        const hubspotWrapper = formParent.current.querySelector('.hs_cos_wrapper_type_form');
        const form = hubspotWrapper.querySelector('form');
        const numbers = formParent.current.querySelectorAll('.hs-number');

        numbers.forEach((num) => {
          const input = num.querySelector('input');
          input.classList.add('body-2');
        });

        form.classList.add('body-2');

        const titleClasses = ['c-reusable-form__form-title', 'h3-roobert', 'color-black'];
        const inputWrappersClasses = ['c-form__input-container'];

        title.classList.add(...titleClasses);

        errors.forEach((error) =>
          error.classList.add(...['c-error-message', 'body-3', 'color-red']),
        );
        richtext.forEach((text) =>
          text.classList.add(...['c-reusable-form__disclaimer', 'body-3', 'color-black']),
        );
        submit.forEach((el) =>
          el.classList.add(...['c-cta-button', 'primary', 'color-white', 'cta']),
        );

        inputWrappers.forEach((wrapper) => {
          const input = wrapper.querySelector('.hs-input');
          wrapper.classList.add(...inputWrappersClasses);

          input.classList.add(...['body-2', 'color-black']);

          switch (true) {
            case input.type === 'checkbox':
              wrapper.classList.add(...['checkbox', 'body-2']);
              break;
            case input.type === 'hidden':
              wrapper.classList.add('hidden');
              break;
            case input.tagName === ('SELECT' || 'select'):
              const inputClassesToAdd = ['body-2'];
              const wrapperClassesToAdd = ['select-element', 'translucent', 'c-select', 'body-2'];
              wrapper.classList.add(...wrapperClassesToAdd);
              input.classList.add(...inputClassesToAdd);
              break;
          }
        });

        labels.forEach((label) => {
          const classesToAdd = ['c-form__input-label', 'eyebrow-2'];
          label.classList.add(...classesToAdd);
        });

        setLoading(false);
      };
    }
  }, []);

  // We need to provide backwards-compatibility with components that are
  // already utilizing hardcoded fields.
  const rows = [
    <div className="c-form__row">
      <div className="c-form__input-container">
        <label htmlFor="firstname" className="c-form__input-label eyebrow-2">
          FIRST NAME
        </label>
        <input
          id={'firstname'}
          name={'firstname'}
          className={`body-2 color-black c-form__input ${errors['firstname'] ? 'error' : ''}`}
          type="text"
          ref={register({
            required: true,
          })}
          onBlur={() => trigger('firstname')}
        />
        {errors['firstname'] && <ErrorMessage message={'First name is required'} />}
      </div>
      <div className="c-form__input-container">
        <label htmlFor="lastname" className="c-form__input-label eyebrow-2">
          LAST NAME
        </label>
        <input
          id={'lastname'}
          name={'lastname'}
          className={`body-2 color-black c-form__input ${errors['lastname'] ? 'error' : ''}`}
          type="text"
          ref={register({
            required: true,
          })}
          onBlur={() => trigger('lastname')}
        />
        {errors['lastname'] && <ErrorMessage message={'Last name is required'} />}
      </div>
    </div>,
    <div className="c-form__row">
      <div className="c-form__input-container">
        <label htmlFor="email" className="c-form__input-label eyebrow-2">
          EMAIL
        </label>
        <input
          id={'email'}
          name={'email'}
          className={`body-2 color-black c-form__input ${errors['email'] ? 'error' : ''}`}
          type="email"
          ref={register({
            required: true,
            pattern: emailPattern,
          })}
          onBlur={() => trigger('email')}
        />
        {errors['email'] && <ErrorMessage message={'Please enter a valid email address'} />}
      </div>
      <div className="c-form__input-container">
        <label htmlFor="company" className="c-form__input-label eyebrow-2">
          COMPANY NAME
        </label>
        <input
          id={'company'}
          name={'company'}
          className={`body-2 color-black c-form__input ${errors['company'] ? 'error' : ''}`}
          type="text"
          ref={register({
            required: true,
          })}
          onBlur={() => trigger('company')}
        />
        {errors['company'] && <ErrorMessage message={'Company name is required'} />}
      </div>
    </div>,
    <div className="c-form__row">
      <CTAButton
        type={'primary'}
        name={'submit'}
        text={ctaText}
        additionalClasses={'c-form__submit cta'}
        isButton={true}
        buttonType={'submit'}
        disabled={!formState.isValid}
      />
    </div>,
  ];

  const regex = /#(\S)/g;
  const anchorId = anchor ? anchor.replace(regex, '$1') : null;

  return (
    <div id={`${anchorId}`} className={`c-reusable-form background-color-${backgroundColor}`}>
      <div className="c-reusable-form__inner">
        <div className="c-reusable-form__header">
          {title && (
            <div className={`c-reusable-form__title h4-right-grotesk color-${textColor}`}>
              <h4>{title}</h4>
            </div>
          )}
          {subtitle && isLandingPage ? (
            <div
              className={`c-reusable-form__subtitle body-1 color-${textColor}`}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            ></div>
          ) : (
            <div className={`c-reusable-form__subtitle body-1 color-${textColor}`}>
              <p>{subtitle}</p>
            </div>
          )}
        </div>
        <div
          className={`c-reusable-form__form-text-container ${
            items.length === 0 ? 'center-form' : ''
          }`}
        >
          <div ref={formParent} className="c-reusable-form__form-container background-color-white">
            {loading && <Loader />}
            {!loading && !elementChildren.length && !formComplete ? (
              <>
                <div className="c-reusable-form__form-title h3-roobert color-black">
                  <h3>{formTitle}</h3>
                </div>
                <Form
                  handleSubmit={handleSubmit}
                  guid={formGuid}
                  ctaText={ctaText}
                  rows={rows}
                  setFormComplete={setFormComplete}
                  setStatus={setStatus}
                />
                <div
                  className="c-reusable-form__disclaimer body-3 color-black"
                  dangerouslySetInnerHTML={{ __html: disclaimer }}
                ></div>
              </>
            ) : (
              <div
                className="c-reusable-form__hubspot-contents background-color-white"
                dangerouslySetInnerHTML={{ __html: elementChildren }}
              ></div>
            )}
            {!elementChildren.length && formComplete && (
              <FormSuccessMessage title={successTitle} message={successMessage} />
            )}
          </div>
          {items.length > 0 && (
            <div className="c-reusable-form__list-container">
              <div className={`c-reusable-form__list-title color-${textColor} h3-roobert`}>
                <h3>{listTitle}</h3>
              </div>
              <ul className={`c-reusable-form__list color-${textColor}`}>
                {items.map((item) => {
                  return isLandingPage ? (
                    <li
                      key={item.text}
                      className={`c-reusable-form__list-item color-${textColor} body-1`}
                      dangerouslySetInnerHTML={{ __html: item.text }}
                    ></li>
                  ) : (
                    <li
                      key={item.text}
                      className={`c-reusable-form__list-item color-${textColor} body-1`}
                    >
                      <p>{item.text}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReusableForm;
